import type {
	DomainId,
	Practice,
	PracticeId,
	Question,
	SituationId,
} from "@/pages/AssessmentPlayground";

export type EvidenceSourceId =
	| "phq-gad"
	| "who5"
	| "perma"
	| "cbti"
	| "cbt"
	| "mindfulness"
	| "behavioral-activation"
	| "positive-interventions"
	| "self-compassion"
	| "social-support"
	| "crisis-support"
	| "competitor-observation";

export type EvidenceSource = {
	id: EvidenceSourceId;
	title: string;
	type: "标准量表" | "治疗框架" | "积极心理学" | "安全伦理" | "竞品观察";
	url: string;
	useFor: string;
	caution: string;
};

export type ScientificQuestionReference = {
	id: string;
	sourceId: EvidenceSourceId;
	title: string;
	questionStem: string;
	answerFormat: "单选" | "多选" | "打分" | "量表";
	timeWindow: string;
	relatedDomainIds: DomainId[];
	relatedSituationIds: SituationId[];
	testedState: string;
	detectedState: string;
	emotionTags: string;
	courseCategory: string;
	exerciseType: string;
	trainingTheory: string;
	scoringNote: string;
	productizedUse: string;
	caution: string;
};

export const evidenceSources: Record<EvidenceSourceId, EvidenceSource> = {
	"phq-gad": {
		id: "phq-gad",
		title: "PHQ / GAD-7 维度",
		type: "标准量表",
		url: "https://www.phqscreeners.com/select-screener",
		useFor: "低落、兴趣下降、睡眠、疲惫、焦虑担心、功能影响和风险信号。",
		caution: "前台题目只能借鉴维度，不能冒充诊断；正式使用原题需要保留量表说明和评分口径。",
	},
	who5: {
		id: "who5",
		title: "WHO-5 幸福感指数",
		type: "标准量表",
		url: "https://www.psykiatri-regionh.dk/who-5/who-5-questionnaires/Pages/default.aspx",
		useFor: "舒服、放松、精力、醒来恢复、生活兴趣等正向状态追踪。",
		caution: "适合周期性跟踪幸福感，不适合单独解释具体情绪原因。",
	},
	perma: {
		id: "perma",
		title: "PERMA 幸福五要素",
		type: "积极心理学",
		url: "https://www.authentichappiness.sas.upenn.edu/learn/wellbeing",
		useFor: "积极情绪、投入、人际、意义、成就这五类长期训练方向。",
		caution: "PERMA 是发展目标，不是情绪识别模型；不能只靠 PERMA 判断用户当下状态。",
	},
	cbti: {
		id: "cbti",
		title: "CBT-I 睡眠干预框架",
		type: "治疗框架",
		url: "https://www.acpjournals.org/doi/10.7326/M15-2175",
		useFor: "入睡难、睡眠维持、作息节律、刺激控制、睡前认知过载。",
		caution: "轻训练只能做低风险动作；长期严重失眠应转向完整 CBT-I 或专业睡眠评估。",
	},
	cbt: {
		id: "cbt",
		title: "CBT 认知与问题解决",
		type: "治疗框架",
		url: "https://en.wikipedia.org/wiki/Cognitive_behavioral_therapy",
		useFor: "担忧外化、可控不可控、事实想法分离、降低灾难化和任务拆解。",
		caution: "不能把复杂治疗简化成一句建议；前台只承载低风险自助练习。",
	},
	mindfulness: {
		id: "mindfulness",
		title: "正念与身体调节",
		type: "治疗框架",
		url: "https://en.wikipedia.org/wiki/Mindfulness-based_stress_reduction",
		useFor: "呼吸、身体扫描、接地、情绪命名和高唤醒状态稳定。",
		caution: "强烈惊恐或创伤反应时不宜强推闭眼冥想，应优先开放眼睛的稳定动作和真人支持。",
	},
	"behavioral-activation": {
		id: "behavioral-activation",
		title: "行为激活",
		type: "治疗框架",
		url: "https://en.wikipedia.org/wiki/Behavioral_activation",
		useFor: "低动力、拖延、低落、回避和最小行动启动。",
		caution: "动作必须足够小，不能把用户推向更大的失败感。",
	},
	"positive-interventions": {
		id: "positive-interventions",
		title: "积极心理干预",
		type: "积极心理学",
		url: "https://en.wikipedia.org/wiki/Gratitude_journal",
		useFor: "感恩、三件好事、小确幸、善意行动和正向注意力训练。",
		caution: "强烈痛苦时不能强行积极化，应先稳定情绪和身体。",
	},
	"self-compassion": {
		id: "self-compassion",
		title: "自我慈悲",
		type: "治疗框架",
		url: "https://en.wikipedia.org/wiki/Self-compassion",
		useFor: "自责、羞耻、完美主义、低自信和自我攻击。",
		caution: "适合降低二次内耗，但不能替代任务结构、关系边界或专业治疗。",
	},
	"social-support": {
		id: "social-support",
		title: "社会支持与关系连接",
		type: "治疗框架",
		url: "https://en.wikipedia.org/wiki/Social_support",
		useFor: "孤独、关系断联、需求表达、边界和低成本连接动作。",
		caution: "涉及控制、暴力或高冲突关系时，连接练习应让位于安全计划。",
	},
	"crisis-support": {
		id: "crisis-support",
		title: "危机支持与专业转介",
		type: "安全伦理",
		url: "https://www.who.int/news-room/fact-sheets/detail/suicide",
		useFor: "自伤、失控、强烈绝望、明确风险和自助无效。",
		caution: "这不是练习库问题，而是产品责任边界；必须优先真人和专业支持。",
	},
	"competitor-observation": {
		id: "competitor-observation",
		title: "竞品观察",
		type: "竞品观察",
		url: "https://www.finchcare.com/",
		useFor: "题型、入口话术、练习形态、反馈闭环和用户接受度假设。",
		caution: "只能记录机制和分类，不能照搬竞品题目文案。",
	},
};

export const scientificQuestionReferences: ScientificQuestionReference[] = [
	{
		id: "science-phq-interest",
		sourceId: "phq-gad",
		title: "PHQ-9 兴趣/愉快感维度",
		questionStem: "近两周，做事兴趣或愉快感是否明显减少？",
		answerFormat: "打分",
		timeWindow: "近两周",
		relatedDomainIds: ["stress", "positive", "action"],
		relatedSituationIds: ["low_mood", "positive_low_joy", "action_low_motivation"],
		testedState: "兴趣下降、愉快感减少、低落风险",
		detectedState: "情绪低落、缺少正向体验、低动力",
		emotionTags: "低落无力, 平静积极, 拖延失控",
		courseCategory: "情绪觉察, 行为激活, 积极体验",
		exerciseType: "情绪觉察, 行动启动, 感恩积极",
		trainingTheory: "PHQ-9 维度, 行为激活, 积极心理干预",
		scoringNote: "频率越高，低落和缺少正向体验权重越高；不能单题判断抑郁。",
		productizedUse: "用于确认用户选择“情绪低落”后是否只是当天波动，还是存在兴趣下降信号。",
		caution: "前台应使用改写题和轻筛查语言，不输出诊断结论。",
	},
	{
		id: "science-phq-low-mood",
		sourceId: "phq-gad",
		title: "PHQ-9 低落/绝望维度",
		questionStem: "近两周，是否经常低落、沮丧，或觉得没有希望？",
		answerFormat: "打分",
		timeWindow: "近两周",
		relatedDomainIds: ["stress", "safety"],
		relatedSituationIds: ["low_mood", "safety_risk"],
		testedState: "低落、沮丧、绝望感",
		detectedState: "情绪低落、绝望风险、安全风险",
		emotionTags: "低落无力",
		courseCategory: "情绪觉察, 安全支持",
		exerciseType: "情绪觉察, 安全支持",
		trainingTheory: "PHQ-9 维度, 风险分流, 危机支持",
		scoringNote: "持续时间和影响程度越高，越需要安全确认和专业支持提示。",
		productizedUse: "用于低落路径的持续时间、影响程度和安全确认。",
		caution: "涉及绝望或自伤风险时，必须优先安全转介。",
	},
	{
		id: "science-phq-sleep-energy",
		sourceId: "phq-gad",
		title: "PHQ-9 睡眠/疲惫维度",
		questionStem: "近两周，睡眠或精力是否明显变差？",
		answerFormat: "打分",
		timeWindow: "近两周",
		relatedDomainIds: ["sleep", "body", "stress"],
		relatedSituationIds: ["sleep_tired", "body_fatigue", "sleep_onset", "low_mood"],
		testedState: "睡眠变化、疲惫、精力下降",
		detectedState: "睡眠恢复差、身体疲惫、低落伴随信号",
		emotionTags: "睡眠疲惫, 低落无力, 身体照顾",
		courseCategory: "睡眠修复, 身体照顾, 行为激活",
		exerciseType: "睡眠修复, 身体照顾, 身体活动",
		trainingTheory: "PHQ-9 维度, WHO-5 能量维度, 行为激活",
		scoringNote: "睡眠和精力是跨领域信号，需要结合入睡、夜醒、恢复感和低落题共同判断。",
		productizedUse: "用于区分低落、睡眠恢复差和身体能量不足。",
		caution: "不要把疲惫直接归因为心理问题；需要保留身体健康和睡眠问题边界。",
	},
	{
		id: "science-gad-worry",
		sourceId: "phq-gad",
		title: "GAD-7 担忧失控维度",
		questionStem: "过去两周，是否经常控制不住担心？",
		answerFormat: "打分",
		timeWindow: "过去两周",
		relatedDomainIds: ["stress", "sleep"],
		relatedSituationIds: ["stress_anxiety", "sleep_rumination", "stress_overload"],
		testedState: "反复担心、担忧失控、焦虑强度",
		detectedState: "焦虑担心、睡前想太多、压力过载",
		emotionTags: "焦虑压力, 睡眠疲惫",
		courseCategory: "压力管理, 认知整理, 睡前卸载",
		exerciseType: "认知梳理, 呼吸放松",
		trainingTheory: "GAD-7 维度, CBT 担忧外化, 正念稳定",
		scoringNote: "担忧频率和失控感越高，焦虑担心权重越高；睡前出现时同时命中睡前反刍。",
		productizedUse: "用于压力/焦虑路径和睡前想太多路径的确认题。",
		caution: "不能把担忧等同于焦虑障碍诊断。",
	},
	{
		id: "science-gad-restless-irritable",
		sourceId: "phq-gad",
		title: "GAD-7 紧张/坐立不安/易怒维度",
		questionStem: "过去两周，是否经常紧张、坐立不安，或更容易烦躁？",
		answerFormat: "打分",
		timeWindow: "过去两周",
		relatedDomainIds: ["stress", "body"],
		relatedSituationIds: ["stress_anxiety", "stress_irritable", "body_tension", "panic_signal"],
		testedState: "身体高唤醒、紧张、烦躁易怒",
		detectedState: "焦虑担心、烦躁易怒、身体警报感",
		emotionTags: "焦虑压力, 烦躁愤怒",
		courseCategory: "身体稳定, 压力管理",
		exerciseType: "呼吸放松, 身体调节",
		trainingTheory: "GAD-7 维度, 正念身体调节, 情绪调节",
		scoringNote: "高唤醒信号越强，越优先推荐身体调节，而不是直接做认知练习。",
		productizedUse: "用于区分焦虑担心、烦躁易怒和身体警报感。",
		caution: "强烈惊恐或失控担忧需要安全支持边界。",
	},
	{
		id: "science-who5-positive-affect",
		sourceId: "who5",
		title: "WHO-5 正向情绪/平静维度",
		questionStem: "过去两周，你是否经常感到心情不错、平静或放松？",
		answerFormat: "打分",
		timeWindow: "过去两周",
		relatedDomainIds: ["positive", "stress"],
		relatedSituationIds: ["positive_low_joy", "low_mood", "stress_anxiety"],
		testedState: "正向情绪、平静感、放松感",
		detectedState: "缺少正向体验、低落、压力高",
		emotionTags: "平静积极, 低落无力, 焦虑压力",
		courseCategory: "积极体验, 情绪觉察, 正念放松",
		exerciseType: "感恩积极, 呼吸放松",
		trainingTheory: "WHO-5 幸福感追踪, PERMA 积极情绪, 正念调节",
		scoringNote: "正向体验少时提高积极生活和低落伴随权重；适合周期性跟踪。",
		productizedUse: "用于判断用户是否缺少愉悦、平静和正向体验。",
		caution: "WHO-5 更适合追踪幸福感，不适合单独判断具体原因。",
	},
	{
		id: "science-who5-energy-interest",
		sourceId: "who5",
		title: "WHO-5 精力/兴趣维度",
		questionStem: "过去两周，你是否有精力，且对日常生活有兴趣？",
		answerFormat: "打分",
		timeWindow: "过去两周",
		relatedDomainIds: ["body", "positive", "action"],
		relatedSituationIds: ["body_fatigue", "positive_low_joy", "action_low_motivation"],
		testedState: "精力、恢复感、生活兴趣",
		detectedState: "身体疲惫、低动力、缺少兴趣",
		emotionTags: "身体照顾, 低落无力, 平静积极",
		courseCategory: "身体照顾, 行为激活, 积极体验",
		exerciseType: "身体活动, 行动启动, 感恩积极",
		trainingTheory: "WHO-5 幸福感追踪, 行为激活, 积极心理干预",
		scoringNote: "精力和兴趣低时，不应只推积极练习；先看睡眠、身体照顾和最小行动。",
		productizedUse: "用于低动力、醒来仍疲惫、缺少正向体验的交叉判断。",
		caution: "需要结合睡眠、身体健康和近期压力解释。",
	},
	{
		id: "science-cbti-latency",
		sourceId: "cbti",
		title: "CBT-I 入睡潜伏期维度",
		questionStem: "最近入睡通常需要多久？",
		answerFormat: "单选",
		timeWindow: "最近 3-7 天",
		relatedDomainIds: ["sleep"],
		relatedSituationIds: ["sleep_onset", "sleep_rumination", "body_tension"],
		testedState: "入睡困难、睡前高唤醒",
		detectedState: "入睡难、睡前想太多、身体紧绷",
		emotionTags: "睡眠疲惫, 焦虑压力",
		courseCategory: "睡眠修复, 身体调节, 认知卸载",
		exerciseType: "睡眠修复, 呼吸放松, 认知梳理",
		trainingTheory: "CBT-I, 刺激控制, 睡前放松",
		scoringNote: "超过 30 分钟是入睡难强信号；仍需区分身体高唤醒和认知反刍。",
		productizedUse: "用于睡眠路径确认“入睡难”。",
		caution: "长期严重失眠不应只给轻训练，应提示完整 CBT-I 或专业评估。",
	},
	{
		id: "science-cbti-nightwake",
		sourceId: "cbti",
		title: "CBT-I 睡眠维持维度",
		questionStem: "夜里是否容易醒，醒后是否难再入睡？",
		answerFormat: "单选",
		timeWindow: "最近 3-7 天",
		relatedDomainIds: ["sleep"],
		relatedSituationIds: ["sleep_nightwake", "stress_anxiety", "sleep_rumination"],
		testedState: "夜醒、浅睡、醒后再入睡困难",
		detectedState: "夜醒/浅睡、压力伴随、睡前刺激",
		emotionTags: "睡眠疲惫, 焦虑压力",
		courseCategory: "睡眠修复, 环境调节",
		exerciseType: "睡眠修复, 身体调节",
		trainingTheory: "CBT-I, 睡眠卫生, 刺激控制",
		scoringNote: "夜醒频繁或醒后难睡回去时，提高夜醒/浅睡权重，同时检查手机和环境刺激。",
		productizedUse: "用于区分入睡难和睡眠维持问题。",
		caution: "夜醒可能和身体健康、药物、环境有关，不能只按心理训练处理。",
	},
	{
		id: "science-cbti-rhythm-stimulus",
		sourceId: "cbti",
		title: "CBT-I 作息节律/刺激控制维度",
		questionStem: "睡觉和起床时间是否稳定？睡前是否被手机、任务或刺激内容拖晚？",
		answerFormat: "单选",
		timeWindow: "最近 7 天",
		relatedDomainIds: ["sleep", "order"],
		relatedSituationIds: ["sleep_rhythm", "sleep_phone", "phone_overuse", "order_chaos"],
		testedState: "作息节律、睡前刺激、床与清醒活动绑定",
		detectedState: "作息乱、睡前刷手机、生活秩序乱",
		emotionTags: "睡眠疲惫, 拖延失控",
		courseCategory: "睡眠修复, 生活秩序",
		exerciseType: "睡眠修复, 行动启动",
		trainingTheory: "CBT-I 刺激控制, 睡眠卫生, 行为设计",
		scoringNote: "作息漂移或睡前手机命中时，优先环境和行为动作，而不是只做冥想。",
		productizedUse: "用于睡眠路径和生活秩序路径的共同判断。",
		caution: "倒班、时差和长期昼夜颠倒需要独立方案。",
	},
	{
		id: "science-ba-avoidance",
		sourceId: "behavioral-activation",
		title: "行为激活 回避/活动减少维度",
		questionStem: "低落或焦虑时，你是否明显减少活动、回避任务或一直拖延？",
		answerFormat: "单选",
		timeWindow: "最近 3-7 天",
		relatedDomainIds: ["action", "stress", "positive"],
		relatedSituationIds: ["action_procrastination", "action_low_motivation", "low_mood"],
		testedState: "回避、活动减少、行动启动困难",
		detectedState: "拖延失控、低动力、低落伴随回避",
		emotionTags: "拖延失控, 低落无力, 焦虑压力",
		courseCategory: "行为激活, 行动启动",
		exerciseType: "行动启动, 身体活动",
		trainingTheory: "行为激活, 微习惯, 执行意图",
		scoringNote: "回避越明显，越优先推荐最小行动，而不是要求用户做完整计划。",
		productizedUse: "用于低落、拖延和低动力路径的练习选择。",
		caution: "行动任务需要低门槛，否则会放大失败感。",
	},
	{
		id: "science-ba-reward-mastery",
		sourceId: "behavioral-activation",
		title: "行为激活 愉悦/掌控活动维度",
		questionStem: "最近是否还有让你有一点愉快、成就或掌控感的小活动？",
		answerFormat: "单选",
		timeWindow: "最近 7 天",
		relatedDomainIds: ["positive", "action", "self"],
		relatedSituationIds: ["positive_low_joy", "action_low_motivation", "self_low_confidence"],
		testedState: "愉悦活动、掌控活动、成就感",
		detectedState: "缺少正向体验、低动力、缺乏自信",
		emotionTags: "低落无力, 平静积极, 自责内耗",
		courseCategory: "积极体验, 行为激活, 自我接纳",
		exerciseType: "感恩积极, 行动启动",
		trainingTheory: "行为激活, PERMA 成就, 积极心理干预",
		scoringNote: "缺少愉悦/掌控活动时，可推荐完成清单、短距离走动、感恩或关系连接。",
		productizedUse: "用于判断积极生活练习从成就、愉悦还是连接开始。",
		caution: "强低落时不要强行积极化，先用低门槛行动和支持。",
	},
];

export const evidenceLevelNotes = {
	较强: "来自标准量表、指南或广泛使用的治疗框架，适合优先放进产品。",
	中等: "来自成熟治疗/训练理论，但在本产品里仍需要验证用户接受度。",
	探索: "低风险产品假设或竞品启发，需要用打卡反馈验证，不应作为核心疗效承诺。",
	安全优先: "不是训练效果问题，而是风险处置优先级。",
} as const;

export const modeTheoryNotes: Record<Practice["mode"], { summary: string; reason: string }> = {
	认知: {
		summary: "把混乱想法外化、分类、重评或拆成下一步。",
		reason: "适合脑内循环、担忧、内耗和任务模糊；目标是降低认知负荷。",
	},
	身体: {
		summary: "用呼吸、接地、扫描、移动先降低身体唤醒。",
		reason: "情绪强时身体先被激活，直接讲道理通常不如先把身体带回来。",
	},
	行动: {
		summary: "把问题变成一个几乎不会失败的小动作。",
		reason: "适合低动力、拖延和失控感；通过行动恢复掌控，而不是靠意志力。",
	},
	关系: {
		summary: "恢复连接、表达需求或建立边界。",
		reason: "长期积极生活离不开关系支持；但高冲突场景要先做安全判断。",
	},
	积极: {
		summary: "训练正向注意力、感恩、成就和意义感。",
		reason: "这是积极心理学最直接的部分，但应在用户能承受时使用。",
	},
	记录: {
		summary: "记录情绪、触发点、强度和反馈。",
		reason: "识别和反馈都依赖记录；它是个性化推荐的底层数据。",
	},
	安全: {
		summary: "连接真人支持和专业系统。",
		reason: "自助产品不能承接危机干预；风险信号必须优先升级处理。",
	},
};

export const sourceIdsByDomain: Record<DomainId, EvidenceSourceId[]> = {
	sleep: ["cbti", "mindfulness", "phq-gad"],
	stress: ["phq-gad", "mindfulness", "cbt", "crisis-support"],
	action: ["behavioral-activation", "cbt", "self-compassion"],
	self: ["self-compassion", "cbt", "perma"],
	relationship: ["social-support", "perma", "cbt"],
	body: ["who5", "mindfulness", "behavioral-activation"],
	order: ["cbti", "behavioral-activation", "cbt"],
	positive: ["perma", "who5", "positive-interventions"],
	safety: ["crisis-support", "phq-gad"],
};

const sourceIdsByQuestionPrefix: Array<[string, EvidenceSourceId[]]> = [
	["sleep", ["cbti", "mindfulness", "phq-gad"]],
	["stress", ["phq-gad", "mindfulness", "cbt"]],
	["low-mood", ["phq-gad", "who5", "behavioral-activation"]],
	["positive", ["perma", "who5", "positive-interventions"]],
	["action", ["behavioral-activation", "cbt", "self-compassion"]],
	["self", ["self-compassion", "cbt"]],
	["relationship", ["social-support", "perma", "cbt"]],
	["body", ["who5", "mindfulness", "behavioral-activation"]],
	["order", ["behavioral-activation", "cbt"]],
	["phone", ["behavioral-activation", "cbt", "cbti"]],
	["safety", ["crisis-support", "phq-gad"]],
];

const sourceIdsBySituation: Partial<Record<SituationId, EvidenceSourceId[]>> = {
	sleep_onset: ["cbti", "mindfulness"],
	sleep_rumination: ["cbti", "cbt"],
	sleep_nightwake: ["cbti", "mindfulness"],
	sleep_tired: ["who5", "cbti", "behavioral-activation"],
	sleep_rhythm: ["cbti", "behavioral-activation"],
	sleep_phone: ["cbti", "behavioral-activation"],
	stress_anxiety: ["phq-gad", "cbt", "mindfulness"],
	stress_overload: ["phq-gad", "cbt"],
	stress_irritable: ["mindfulness", "cbt"],
	low_mood: ["phq-gad", "who5", "behavioral-activation"],
	panic_signal: ["phq-gad", "mindfulness", "crisis-support"],
	action_procrastination: ["behavioral-activation", "cbt"],
	action_low_motivation: ["behavioral-activation", "who5"],
	action_unclear_step: ["cbt", "behavioral-activation"],
	perfection_block: ["self-compassion", "cbt"],
	self_criticism: ["self-compassion", "cbt"],
	self_low_confidence: ["self-compassion", "behavioral-activation"],
	relationship_lonely: ["social-support", "perma", "positive-interventions"],
	relationship_conflict: ["social-support", "cbt"],
	body_fatigue: ["who5", "behavioral-activation"],
	body_tension: ["mindfulness", "phq-gad"],
	order_chaos: ["behavioral-activation", "cbt"],
	phone_overuse: ["behavioral-activation", "cbt"],
	positive_low_joy: ["perma", "who5", "positive-interventions"],
	safety_risk: ["crisis-support", "phq-gad"],
};

export const requiredQuestionEvidenceFields = [
	"题目来源",
	"测试状态",
	"评分规则",
	"适用边界",
	"是否专家审核",
];

export const requiredPracticeEvidenceFields = [
	"练习理论",
	"为什么推荐",
	"为什么不是别的",
	"更好替代",
	"安全边界",
];

export function getQuestionEvidenceSources(question: Question) {
	const ids = new Set<EvidenceSourceId>(["competitor-observation"]);

	for (const domainId of question.targetDomains ?? []) {
		for (const sourceId of sourceIdsByDomain[domainId]) {
			ids.add(sourceId);
		}
	}

	for (const situationId of question.targetSituations ?? []) {
		for (const sourceId of sourceIdsBySituation[situationId] ?? []) {
			ids.add(sourceId);
		}
	}

	for (const [prefix, sourceIds] of sourceIdsByQuestionPrefix) {
		if (question.id.startsWith(prefix)) {
			for (const sourceId of sourceIds) {
				ids.add(sourceId);
			}
		}
	}

	if (question.id === "entry" || question.id === "general-impact") {
		ids.add("phq-gad");
		ids.add("who5");
	}

	return Array.from(ids).map((id) => evidenceSources[id]);
}

export function getQuestionReferenceRecords(question: Question) {
	const sourceIds = new Set<EvidenceSourceId>(
		getQuestionEvidenceSources(question).map((source) => source.id),
	);
	sourceIds.delete("competitor-observation");
	const domainIds = new Set<DomainId>(question.targetDomains ?? []);
	const situationIds = new Set<SituationId>(question.targetSituations ?? []);

	for (const option of question.options) {
		for (const domainId of Object.keys(option.domainScores ?? {}) as DomainId[]) {
			domainIds.add(domainId);
		}
		for (const situationId of Object.keys(option.situationScores ?? {}) as SituationId[]) {
			situationIds.add(situationId);
		}
	}

	return scientificQuestionReferences
		.filter((reference) => {
			if (!sourceIds.has(reference.sourceId)) {
				return false;
			}
			const matchesDomain = reference.relatedDomainIds.some((id) => domainIds.has(id));
			const matchesSituation = reference.relatedSituationIds.some((id) => situationIds.has(id));
			return matchesDomain || matchesSituation || question.id === "entry";
		})
		.slice(0, 6);
}

export function getPracticeEvidenceSources(id: PracticeId, practice: Practice) {
	const ids = new Set<EvidenceSourceId>();
	const text = `${id} ${practice.title} ${practice.category} ${practice.mode} ${practice.theory}`;

	if (/安全|危机|专业|风险/.test(text)) {
		ids.add("crisis-support");
	}
	if (/睡眠|CBT-I|作息|入睡|白噪音|起床/.test(text)) {
		ids.add("cbti");
	}
	if (/呼吸|正念|身体扫描|接地|身体调节/.test(text)) {
		ids.add("mindfulness");
	}
	if (/CBT|认知|可控|担忧|问题解决|重评/.test(text)) {
		ids.add("cbt");
	}
	if (/行为激活|行动启动|微习惯|身体活动|掌控感/.test(text)) {
		ids.add("behavioral-activation");
	}
	if (/感恩|积极心理|成就|正向|意义/.test(text)) {
		ids.add("positive-interventions");
		ids.add("perma");
	}
	if (/自我慈悲|自我对话|温和/.test(text)) {
		ids.add("self-compassion");
	}
	if (/关系|社会支持|边界|连接|沟通/.test(text)) {
		ids.add("social-support");
	}

	if (ids.size === 0) {
		ids.add("competitor-observation");
	}

	return Array.from(ids).map((sourceId) => evidenceSources[sourceId]);
}
